/**
 * AK-938 — «Все сезоны» в селекте сезона шапки, только в разделе «Наука».
 *
 * Опыт ищут через годы («что дал гербицид в прошлом сезоне»), поэтому в «Науке»
 * сезон по умолчанию — «Все сезоны» (low-fi 21.10, утверждён founder). Это НЕ
 * сезон сессии: сессия по-прежнему держит один сезон, и все прочие разделы
 * работают как раньше. Линза — фильтр списка «Науки» поверх сессии:
 *
 *   · `all`     — показываем опыты всех сезонов;
 *   · `session` — сужаем до сезона сессии (человек выбрал год в шапке).
 *
 * Селект сезона показывает пункт «Все сезоны» ТОЛЬКО пока экран «Науки»
 * смонтирован (`useScienceSeasonLensHost`) — в других разделах его нет.
 * Хранилище — модульное (без контекста и роутера), чтобы селект в шапке
 * не зависел от дерева страниц.
 */
import { useEffect, useSyncExternalStore } from 'react';

export type ScienceSeasonLens = 'all' | 'session';

interface LensState {
  lens: ScienceSeasonLens;
  /** Сколько экранов «Науки» смонтировано (0 — линзы в шапке нет). */
  hosts: number;
}

let state: LensState = { lens: 'all', hosts: 0 };
const listeners = new Set<() => void>();

function emit(next: LensState) {
  state = next;
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot(): LensState {
  return state;
}

export function setScienceSeasonLens(lens: ScienceSeasonLens): void {
  if (state.lens !== lens) emit({ ...state, lens });
}

/** Для тестов: вернуть умолчания. */
export function resetScienceSeasonLens(): void {
  emit({ lens: 'all', hosts: 0 });
}

/** Линза в шапке активна (мы на экране «Науки») и её значение. */
export function useScienceSeasonLens(): { active: boolean; lens: ScienceSeasonLens } {
  const s = useSyncExternalStore(subscribe, snapshot, snapshot);
  return { active: s.hosts > 0, lens: s.lens };
}

/** Экран «Науки» включает «Все сезоны» в шапке на время, пока смонтирован. */
export function useScienceSeasonLensHost(): void {
  useEffect(() => {
    emit({ ...state, hosts: state.hosts + 1 });
    return () => emit({ ...state, hosts: Math.max(0, state.hosts - 1) });
  }, []);
}

/** Год для запроса списка: `undefined` — все сезоны. */
export function lensSeasonYear(
  lens: ScienceSeasonLens,
  sessionYear: number | null | undefined,
): number | undefined {
  if (lens === 'all' || sessionYear == null) return undefined;
  return sessionYear;
}
